const LOCAL_MODEL_PREFIX = '/__local-model__/'

type DirectoryFile = File & {
  webkitRelativePath?: string
}

type LocalDirectorySession = {
  files: Map<string, File>
}

export interface LocalDirectoryRegistration {
  readonly entryUrl: string | null
  readonly entryPath: string | null
  readonly totalFiles: number
  /** 幂等释放本次选择持有的 File 引用。 */
  readonly release: () => void
}

const sessions = new Map<string, LocalDirectorySession>()
let installed = false
let originalFetch: typeof fetch | null = null

function normalizeSegments(path: string): string[] {
  const normalized = path.replace(/\\/g, '/')
  const parts = normalized.split('/').filter(Boolean)
  const result: string[] = []

  for (const part of parts) {
    if (part === '.') {
      continue
    }

    if (part === '..') {
      result.pop()
      continue
    }

    result.push(part)
  }

  return result
}

function stripDirectoryRoot(relativePath: string): string {
  const parts = relativePath.replace(/\\/g, '/').split('/').filter(Boolean)
  if (parts.length <= 1) {
    return parts[0] ?? ''
  }

  return parts.slice(1).join('/')
}

function encodeVirtualPath(relativePath: string): string {
  return normalizeSegments(relativePath).map(encodeURIComponent).join('/')
}

function decodeAndNormalizeVirtualPath(relativePath: string): string {
  const decoded = relativePath
    .split('/')
    .filter(Boolean)
    .map((segment) => {
      try {
        return decodeURIComponent(segment)
      } catch {
        return segment
      }
    })
    .join('/')

  return normalizeSegments(decoded).join('/')
}

function getSessionId(): string {
  return `session-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
}

function getMimeType(fileName: string): string {
  const extension = fileName.split('.').pop()?.toLowerCase()

  switch (extension) {
    case 'gltf':
      return 'model/gltf+json'
    case 'glb':
      return 'model/gltf-binary'
    case 'json':
      return 'application/json'
    case 'bin':
    case 'sog':
    case 'ply':
    case 'splat':
      return 'application/octet-stream'
    default:
      return 'application/octet-stream'
  }
}

function parseRangeHeader(rangeHeader: string | null, fileSize: number) {
  if (!rangeHeader) {
    return null
  }

  const match = rangeHeader.match(/^bytes=(\d*)-(\d*)$/i)
  if (!match) {
    return null
  }

  let start = match[1] ? Number(match[1]) : 0
  let end = match[2] ? Number(match[2]) : fileSize - 1

  if (!Number.isFinite(start) || start < 0) {
    start = 0
  }

  if (!Number.isFinite(end) || end >= fileSize) {
    end = fileSize - 1
  }

  if (start > end) {
    return null
  }

  return { start, end }
}

function resolveLocalModelRequest(url: string): { sessionId: string; relativePath: string } | null {
  const parsed = new URL(url, window.location.origin)
  if (!parsed.pathname.startsWith(LOCAL_MODEL_PREFIX)) {
    return null
  }

  const remainder = parsed.pathname.slice(LOCAL_MODEL_PREFIX.length)
  const [sessionId, ...pathSegments] = remainder.split('/').filter(Boolean)
  if (!sessionId || pathSegments.length === 0) {
    return null
  }

  const relativePath = decodeAndNormalizeVirtualPath(pathSegments.join('/'))
  if (!relativePath) {
    return null
  }

  return { sessionId, relativePath }
}

async function createFileResponse(file: File, request: Request): Promise<Response> {
  const range = parseRangeHeader(request.headers.get('range'), file.size)
  const headers = new Headers({
    'Content-Type': getMimeType(file.name),
    'Accept-Ranges': 'bytes',
    'Access-Control-Allow-Origin': '*',
    'Access-Control-Expose-Headers': 'Content-Range, X-Share3D-Source-Kind',
    'X-Share3D-Source-Kind': 'local-file',
  })

  if (!range) {
    headers.set('Content-Length', String(file.size))
    return new Response(file, {
      status: 200,
      headers,
    })
  }

  const chunk = file.slice(range.start, range.end + 1)
  headers.set('Content-Length', String(range.end - range.start + 1))
  headers.set('Content-Range', `bytes ${range.start}-${range.end}/${file.size}`)

  return new Response(chunk, {
    status: 206,
    headers,
  })
}

export function installLocalVirtualFileFetch(): void {
  if (installed) {
    return
  }

  originalFetch = globalThis.fetch.bind(globalThis)

  globalThis.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const request = input instanceof Request ? new Request(input, init) : new Request(input, init)
    const resolved = resolveLocalModelRequest(request.url)

    if (!resolved) {
      return originalFetch!(input, init)
    }

    const session = sessions.get(resolved.sessionId)
    const file = session?.files.get(resolved.relativePath)

    if (!session || !file) {
      return new Response(`Local model file not found: ${resolved.relativePath}`, {
        status: 404,
      })
    }

    return createFileResponse(file, request)
  }

  installed = true
}

/** 固化本次文件选择，并清空 input 以允许再次选择同名文件。 */
export function consumeLocalFileSelection(input: HTMLInputElement): File[] {
  const files = Array.from(input.files ?? [])
  input.value = ''
  return files
}

export function registerLocalModelDirectory(
  files: FileList | File[],
  entryFileNames: string[],
): LocalDirectoryRegistration {
  return registerLocalDirectoryEntry(files, {
    entryFileNames,
  })
}

export function registerLocalDirectoryEntry(
  files: FileList | File[],
  options: {
    entryFileNames?: string[]
    entryExtensions?: string[]
  } = {},
): LocalDirectoryRegistration {
  const normalizedFiles = Array.from(files as ArrayLike<DirectoryFile>)
  if (normalizedFiles.length === 0) {
    return { entryUrl: null, entryPath: null, totalFiles: 0, release: () => undefined }
  }

  const fileMap = new Map<string, File>()

  for (const file of normalizedFiles) {
    const rawRelativePath = file.webkitRelativePath || file.name
    const strippedPath = stripDirectoryRoot(rawRelativePath)
    const normalizedPath = normalizeSegments(strippedPath).join('/')

    if (!normalizedPath) {
      continue
    }

    fileMap.set(normalizedPath, file)
  }

  const lowerCaseEntries = (options.entryFileNames ?? []).map((item) => item.toLowerCase())
  const lowerCaseExtensions = (options.entryExtensions ?? []).map((item) =>
    item.replace(/^\./, '').toLowerCase(),
  )

  let entryPath: string | null = null
  if (lowerCaseEntries.length > 0) {
    for (const candidate of fileMap.keys()) {
      const lowerCandidate = candidate.toLowerCase()
      if (lowerCaseEntries.includes(lowerCandidate)) {
        entryPath = candidate
        break
      }
    }
  }

  if (!entryPath && lowerCaseEntries.length > 0) {
    for (const candidate of fileMap.keys()) {
      const fileName = candidate.split('/').pop()?.toLowerCase()
      if (fileName && lowerCaseEntries.includes(fileName)) {
        entryPath = candidate
        break
      }
    }
  }

  if (!entryPath && lowerCaseExtensions.length > 0) {
    for (const candidate of fileMap.keys()) {
      const extension = candidate.split('.').pop()?.toLowerCase()
      if (extension && lowerCaseExtensions.includes(extension)) {
        entryPath = candidate
        break
      }
    }
  }

  if (!entryPath) {
    return {
      entryUrl: null,
      entryPath: null,
      totalFiles: fileMap.size,
      release: () => undefined,
    }
  }

  const sessionId = getSessionId()
  sessions.set(sessionId, { files: fileMap })
  let released = false

  return {
    entryUrl: `${window.location.origin}${LOCAL_MODEL_PREFIX}${sessionId}/${encodeVirtualPath(entryPath)}`,
    entryPath,
    totalFiles: fileMap.size,
    release: () => {
      if (released) return
      released = true
      sessions.delete(sessionId)
    },
  }
}
